import {
  Stack,
  styled,
  chipClasses,
  Button,
  inputBaseClasses,
  formControlClasses,
  buttonBaseClasses,
  Box,
  tablePaginationClasses,
  checkboxClasses,
  typographyClasses,
} from "@mui/material";
import { gridClasses } from "@mui/x-data-grid";
import { styledTextFieldClasses } from "@hyro-components/fields/text-field/HyroTextField.styles";

export const StyledContainer = styled(Stack, {
  shouldForwardProp: (props) => !["stickyActions", "clickable", "disableHover", "stretchHeight", "alwaysShowScroll", "minHeight", "hasSelectedRows", "selectedColumnIndex", "enableBulkEdit"].includes(props)
})(({ stickyActions, clickable, disableHover, stretchHeight, alwaysShowScroll, minHeight, hasSelectedRows, selectedColumnIndex, enableBulkEdit, theme }) => ({
  ...(stretchHeight ? { flex: 1 } : {}),
  [`& .${gridClasses.root}`]: {
    backgroundColor: theme.palette.system[0],
    minHeight: `${minHeight ? `${minHeight}px` : "457px"}`,
    [`& .${gridClasses.columnHeaders}`]: {
      "&:hover": {
        [`& .${gridClasses.columnHeader}[aria-colindex="1"] .${buttonBaseClasses.root}`]: {
          visibility: "visible"
        }
      }
    },
    [`& .${gridClasses.columnHeader}`]: {
      '&[aria-colindex="1"]': {
        paddingLeft: enableBulkEdit ? "4px" : "24px",
        [`& .${buttonBaseClasses.root}`]: {
          visibility: hasSelectedRows ? "visible" : "hidden"
        }
      },
      cursor: "default",
      "&:focus-within": { outline: "none" },
      '&[data-field="actions"]': {
        position: "sticky",
        right: -1,
        backgroundColor: theme.palette.system[0]
      }
    },
    [`& .${gridClasses.columnHeaderTitle}`]: {
      fontWeight: "600"
    },
    [`& .${gridClasses.columnHeaderTitleContainer}`]: {
      overflow: "visible",
      gap: theme.spacing(1)
    },
    [`& .${gridClasses.columnHeaderTitleContainerContent}`]: {
      overflow: "visible",
      color: theme.palette.text[800]
    },
    // Sorting Icon
    [`& .${gridClasses.iconButtonContainer} .${buttonBaseClasses.root}`]: {
      width: "20px",
      height: "20px",
      backgroundColor: "transparent"
    },
    [`& .${gridClasses["columnHeader--sorted"]} .${buttonBaseClasses.root}`]: {
      "& path": {
        stroke: theme.palette.primary[500]
      }
    },
    [`& .${gridClasses.cell}`]: {
      padding: "6px 10px 6px 10px",
      minHeight: "56px",
      display: "flex",
      alignItems: "center",
      overflow: "visible",
      color: theme.palette.text[800],
      '&[aria-colindex="1"]': { paddingLeft: "14px !important" },
      "&:focus, &:focus-within": { outline: "none" },
      "&.error-cell .cell-container": { outline: "1px solid #EF9AA1" },
      '&[data-field="actions"]': {
        position: "sticky",
        right: -1,
        zIndex: 1,
        display: "grid",
        placeContent: "center",
        backgroundColor: "#fff",
        boxShadow: stickyActions ? "-2px 0px 2px 2px rgba(102, 102, 102, 0.01), -4px 0px 8px 0px rgba(85, 85, 85, 0.05)" : "none"
      },
      [`&.${gridClasses.cellSkeleton}`]: {
        "&:nth-of-type(1)": {
          paddingLeft: "24px"
        },
        minHeight: "57px"
      }
    },
    [`& .${gridClasses.row}`]: {
      "&[aria-selected=true]": {
        [`& [data-colindex='${selectedColumnIndex}']`]: {
          backgroundColor: "#F2F2F4"
        },
        backgroundColor: "#F9FAFD",
        "&:hover": { backgroundColor: "#F9FAFD" },
        [`& .${gridClasses["cell--editing"]}`]: {
          backgroundColor: "#F9FAFD"
        },
        "& [data-field='actions']": {
          backgroundColor: "#F9FAFD"
        },
        [`& .${inputBaseClasses.root}`]: {
          backgroundColor: "#F9FAFD"
        }
      },

      backgroundColor: "#fff",
      "&:hover": {
        backgroundColor: "#fff",
        [`& [data-colindex="0"] .${buttonBaseClasses.root}`]: {
          visibility: "visible"
        }
      },
      [`&:hover:not(:has(.${gridClasses["cell--editing"]}))`]: {
        backgroundColor: disableHover ? "#fff" : "#f7f7f8",
        '[data-field="actions"]': {
          backgroundColor: disableHover ? "#fff" : "#f7f7f8"
        }
      },

      ...(clickable && {
        cursor: "pointer",
        [`& .${chipClasses.root}`]: { cursor: "pointer" }
      })
    },
    ["& [data-colindex=\"0\"]"]: {
      [`& .${buttonBaseClasses.root}`]: {
        visibility: hasSelectedRows ? "visible" : "hidden"
      }
    },
    [`& .${gridClasses.columnSeparator}`]: {
      [`&:not(.${gridClasses["columnSeparator--resizable"]})`]: {
        display: "none"
      }
    },
    [`& .${gridClasses.cell} .${inputBaseClasses.root}`]: {
      zIndex: 1,
      borderRadius: "8px",
      padding: "10px 0",
      width: "100%",
      "&:focus": { outline: "none" },
      "& fieldset": { border: "none", outline: "none" }
    },
    [`& .${gridClasses.cell} .${inputBaseClasses.input}`]: {
      padding: "0 10px",
      width: "100%"
    },
    [`& .${gridClasses["cell--editable"]}`]: {
      position: "relative",
      cursor: "pointer",
      [`& .${chipClasses.root}`]: { cursor: "pointer" },
      "&:hover .list-arrow-down": { display: "block" }
    },
    [`& .${formControlClasses.root}`]: {
      width: "100%"
    },
    [`& .${gridClasses.scrollbar}`]: {
      scrollbarColor: "auto",
      left: "2px",
      right: "2px",
      bottom: "2px",
      height: "8px",
      width: "calc(100% - 4px)",
      "--size": "8px"
    },
    ...(alwaysShowScroll && {
      "& ::-webkit-scrollbar": {
        height: "8px"
      }
    }),
    "& ::-webkit-scrollbar-thumb": {
      background: theme.palette.grey[200],
      borderRadius: "8px"
    },
    [`& .${gridClasses.cell}.${gridClasses["cell--editing"]}`]: {
      padding: "6px 10px 6px 10px",
      boxShadow: "none",
      "&:focus-within": { outline: "none !important" },
      [`& .${inputBaseClasses.root}.${inputBaseClasses.focused}:not(.${inputBaseClasses.error})`]: {
        outline: `2px solid ${theme.palette.primary[500]}`, outlineOffset: "-2px"
      },
      [`& .${inputBaseClasses.root}.${inputBaseClasses.error}`]: {
        outline: "1px solid #EF9AA1", outlineOffset: "-1px"
      }
    },
    [`& .${gridClasses["cell--editing"]}`]: { boxShadow: "none" },
    [`& .${tablePaginationClasses.toolbar}`]: {
      color: theme.palette.text[800]
    }
  }
}));

export const AddNewRowButton = styled(Button)(({ theme }) => ({
  height: "59px",
  width: "var(--DataGrid-width)",
  justifyContent: "flex-start",
  padding: "12px 24px",
  borderRadius: 0,
  borderTop: "1px solid rgba(224, 224, 224, 1)",
  position: "sticky",
  left: 0,
  [`&.${buttonBaseClasses.root}:hover`]: { backgroundColor: theme.palette.system[50] }
}));

export const StyledCellContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isEditable" && prop !== "isDisabledColumn"
})(({ theme, isEditable, isDisabledColumn }) => ({
  position: "relative",
  display: "flex",
  alignItems: "center",
  minHeight: "44px",
  width: "100%",
  padding: "4px 10px",
  borderRadius: "8px",
  lineHeight: "24px",
  outlineOffset: "-1px",
  color: isDisabledColumn ? theme.palette.grey[600] : "inherit",
  "&:hover": {
    outline: isEditable ? "1px solid #2D2C443D" : "none"
  },
  [`& .${styledTextFieldClasses.copyIcon}`]: {
    display: "none",
    position: "absolute",
    right: 8,
    top: "50%",
    transform: "translateY(-50%)"
  },
  [`&:hover .${styledTextFieldClasses.copyIcon}`]: {
    display: "block"
  }
}));

export const getAnalyticsTableSx = (theme, {
  borderRadius,
  maxHeight,
  loading = false,
  loadingHeight = maxHeight,
  cellPadding,
  headerPadding = cellPadding,
  headerColor = theme.palette.grey[600]
} = {}) => ({
  borderRadius,
  ...(loading ? { maxHeight: loadingHeight, overflow: "hidden" } : { maxHeight }),
  [`& .${gridClasses.scrollbar}`]: {
    display: "none"
  },
  [`& .${gridClasses.virtualScroller}`]: {
    scrollbarWidth: "thin",
    "&::-webkit-scrollbar": {
      display: "block",
      width: "8px",
      height: "8px"
    },
    "&::-webkit-scrollbar-thumb": {
      background: theme.palette.grey[200],
      borderRadius: "8px"
    }
  },
  [`&& .${gridClasses.cell}`]: {
    padding: cellPadding
  },
  [`&& .${gridClasses.columnHeader}`]: {
    padding: headerPadding
  },
  [`& .${gridClasses.columnHeader} .${typographyClasses.root}`]: {
    ...theme.typography.subtitle2,
    color: headerColor
  }
});
